import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  IconButton,
  Tooltip,
  Stack,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from '@mui/material';
import {
  CheckCircle as PaidIcon,
  Cancel as UnpaidIcon,
  Timelapse as PartialIcon,
  Print as PrintIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import { stockExitsAPI, stockReturnsAPI, auditLogsAPI } from '../../../services/api';
import { useClientsDropdown } from '../../../hooks/queries/useDropdowns';
import { StockExitNew as StockExit, StockExitItem } from '../../../types/stockExitNew';
import { StockReturnNew as StockReturn } from '../../../types/stockReturnNew';
import { Client } from '../../../types/client';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import { extractErrorMessage } from '../../../utils/errorHandler';
import usePdfPreview from '../../../hooks/usePdfPreview';
import CustomTablePagination from '../../../components/CustomTablePagination';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
// generateClientSituationPDF is dynamically imported in handlePrint so the
// jspdf chunk only loads when the user requests a PDF.

const STOCK_COLOR = '#2196F3';

// All column filters are applied client-side over the assembled exits+returns.
const CLIENT_TRACKING_FILTER_SCHEMA = {
  date:        { type: 'dateRange',   paramFrom: 'date_from',  paramTo: 'date_to' },
  bon_number:  { type: 'text',        param: 'bon_number' },
  reference:   { type: 'text',        param: 'reference' },
  designation: { type: 'text',        param: 'designation' },
  category:    { type: 'text',        param: 'category' },
  unit:        { type: 'text',        param: 'unit' },
  quantity:    { type: 'numberRange', paramMin: 'quantity_min',    paramMax: 'quantity_max' },
  prix_sortie: { type: 'numberRange', paramMin: 'prix_sortie_min', paramMax: 'prix_sortie_max' },
  total:       { type: 'numberRange', paramMin: 'total_min',       paramMax: 'total_max' },
  service:     { type: 'text',        param: 'service' },
  type:        { type: 'select',      param: 'type' },
} as const;

// Combined type for displaying both exits and returns
type CombinedTransaction = {
  id?: number;
  date: string;
  bon_number: string;
  article_reference?: string;
  article_designation?: string;
  category?: string;
  unit?: string;
  quantity: number;
  prix_sortie: number;
  total: number;
  service_name?: string;
  is_paid: boolean;
  client_name?: string;
  type: 'exit' | 'return';
  // Bon-level payment figures (advance + later payments). Same value on every
  // article line of a Bon — rendered once via rowSpan. Undefined for returns.
  total_paid?: number;
  remaining?: number;
};

// The stock-exit / stock-return list endpoints page at `limit=100` by default
// (hard max 1000). `getAll()` sent no skip/limit, so a client with more bons
// than one page was silently truncated: the Payé/Impayé/Retours cards, the
// table, the pagination count AND the printed situation all reported a partial
// balance with nothing on screen to say so. Page through until we've collected
// the `total` the endpoint itself reports.
const PAGE_SIZE = 1000;

// Half a centime — guards against float noise when Python Decimals come back as
// strings and get subtracted, so a fully settled bon can't look 0.0000001 owing.
const CENT = 0.005;

// Bon-level money. Shared by the "Statut de paiement" filter and the KPI cards
// so the two can never drift apart on what "payé" means.
const exitTotal = (exit: StockExit): number =>
  Number(exit.grand_total || (exit.items || []).reduce((sum, item) => sum + Number(item.total || 0), 0));

// Amount actually settled on a bon = creation advance + later payments, clamped
// to the bon total. The backend normalises total_paid (a bon manually marked
// is_paid=Oui is reported as fully paid); the flag is only a legacy fallback.
const exitPaid = (exit: StockExit): number => {
  const total = exitTotal(exit);
  return exit.total_paid !== undefined && exit.total_paid !== null
    ? Math.min(Math.max(Number(exit.total_paid), 0), total)
    : (exit.is_paid ? total : 0);
};

const exitRemaining = (exit: StockExit): number => exitTotal(exit) - exitPaid(exit);

// Payment state of a bon, derived from the AMOUNTS rather than the binary
// is_paid flag. A bon with an avance is neither payé nor impayé — showing it as
// "Impayé" made it look misfiled when it appeared under the "Payé" filter.
// Mirrors the PAYÉ / PARTIEL / IMPAYÉ labels the printed situation uses.
type PaymentState = 'paid' | 'partial' | 'unpaid';

// The three states are MUTUALLY EXCLUSIVE — every bon is in exactly one. That
// is what lets the "Statut de paiement" filter and the print checkboxes select
// bons without overlapping (a bon matching two selections would be listed
// twice under the same BON N°).
const exitState = (exit: StockExit): PaymentState => {
  if (exitRemaining(exit) <= CENT) return 'paid';
  return exitPaid(exit) > CENT ? 'partial' : 'unpaid';
};

const paymentState = (t: CombinedTransaction): PaymentState => {
  // Legacy payloads without the payment figures: fall back to the flag.
  if (t.total_paid === undefined || t.remaining === undefined) {
    return t.is_paid ? 'paid' : 'unpaid';
  }
  if (Number(t.remaining) <= CENT) return 'paid';
  return Number(t.total_paid) > CENT ? 'partial' : 'unpaid';
};

// Amber sits between the green/red poles. Kept distinct from the Retour chip's
// #ff9800, which is a different axis (a return, not a payment state).
const PAYMENT_CHIP: Record<PaymentState, { label: string; color: string }> = {
  paid: { label: 'Payé', color: '#4caf50' },
  partial: { label: 'Partiel', color: '#ed6c02' },
  unpaid: { label: 'Impayé', color: '#f44336' },
};

async function fetchAllPages<T>(
  fetchPage: (skip: number, limit: number) => Promise<{ total: number; items: T[] }>
): Promise<T[]> {
  const first = await fetchPage(0, PAGE_SIZE);
  const items = [...(first.items || [])];
  const total = Number(first.total ?? items.length);
  for (let skip = PAGE_SIZE; skip < total; skip += PAGE_SIZE) {
    const page = await fetchPage(skip, PAGE_SIZE);
    if (!page.items?.length) break;
    items.push(...page.items);
  }
  return items;
}

const ClientTracking: React.FC = () => {
  const { alertState, showError, showSuccess, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  // State management
  const { data: clients = [] } = useClientsDropdown(); // cached landing list
  // `stockExits`/`stockReturns` are the "Statut de paiement" VIEW — they drive
  // the table and the KPI cards. `allExits`/`allReturns` keep the client's full
  // set, because the print dialog carries its own Payé/Impayé/Retours controls
  // and must not be narrowed a second time by the page filter (choosing
  // "Impayé" in the dialog while the page was on "Payé" printed whatever
  // survived both filters, i.e. the paid bons).
  const [stockExits, setStockExits] = useState<StockExit[]>([]);
  const [stockReturns, setStockReturns] = useState<StockReturn[]>([]);
  const [allExits, setAllExits] = useState<StockExit[]>([]);
  const [allReturns, setAllReturns] = useState<StockReturn[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'all' | PaymentState>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hoveredBonNumber, setHoveredBonNumber] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { filters, update, reset, hasActive } = useColumnFilters(
    CLIENT_TRACKING_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [exitToToggle, setExitToToggle] = useState<StockExit | null>(null);

  // Print dialog state
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printOptions, setPrintOptions] = useState({
    includePaid: true,
    includePartial: true,
    includeUnpaid: true,
    includeReturns: true,
  });

  // Client transactions ("search") are always fetched fresh from the backend
  // when a client is selected — only the clients landing list is cached.
  useEffect(() => {
    if (selectedClient) {
      fetchClientStockExits();
    }
  }, [selectedClient, paymentStatus]);

  const fetchClientStockExits = async () => {
    if (!selectedClient) return;

    setLoading(true);
    try {
      // Fetch both exits and returns in parallel — EVERY page, not just the
      // endpoint's default first 100 (see fetchAllPages above).
      const [exitsData, returnsData] = await Promise.all([
        fetchAllPages<StockExit>((skip, limit) =>
          stockExitsAPI.getPaginated(
            skip, limit, undefined, undefined, undefined, undefined, undefined, selectedClient.id
          )
        ),
        fetchAllPages<StockReturn>((skip, limit) =>
          stockReturnsAPI.getPaginated(skip, limit, undefined, selectedClient.id)
        ),
      ]);

      let filteredExits = Array.isArray(exitsData) ? exitsData : [];
      const filteredReturns = Array.isArray(returnsData) ? returnsData : [];

      // Filter by payment status. This narrows the EXITS only — returns are
      // kept in every mode. "Impayé" used to blank them (filteredReturns = [])
      // which zeroed the Retours card and, worse, removed the return credit
      // from the Impayé figure, so the same client showed a different debt
      // under "Tous" than under "Impayé" and the printed situation followed.
      // "Payé" already kept them; the asymmetry was the bug.
      //
      // Each bon sits in exactly one state, so the filter is a plain equality —
      // a partly-paid bon has its own "Partiel" home rather than being hidden
      // (it used to match neither view) or shown under two.
      //
      // The CARDS still split that bon's money across Payé and Impayé: under
      // "Partiel", BN26-1335 puts its 9000.00 avance on the Payé card and its
      // 7099.79 reste on the Impayé card.
      if (paymentStatus !== 'all') {
        filteredExits = filteredExits.filter(exit => exitState(exit) === paymentStatus);
      }

      setAllExits(Array.isArray(exitsData) ? exitsData : []);
      setAllReturns(filteredReturns);
      setStockExits(filteredExits);
      setStockReturns(filteredReturns);
      setPage(0);
    } catch (err) {
      console.error('Erreur lors du chargement des sorties:', err);
      showError('Erreur lors du chargement des sorties');
      setStockExits([]);
      setStockReturns([]);
      setAllExits([]);
      setAllReturns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenConfirmDialog = (exit: StockExit) => {
    setExitToToggle(exit);
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setExitToToggle(null);
  };

  const handleConfirmTogglePaymentStatus = async () => {
    if (!exitToToggle || !exitToToggle.id) return;

    try {
      const response = await stockExitsAPI.togglePaymentStatus(exitToToggle.id);

      showSuccess(response.message);

      // Close dialog and refresh data
      handleCloseConfirmDialog();
      fetchClientStockExits();
    } catch (err: any) {
      showError(extractErrorMessage(err));
      handleCloseConfirmDialog();
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Print dialog handlers
  const handleOpenPrintDialog = () => {
    setPrintDialogOpen(true);
  };

  const handleClosePrintDialog = () => {
    setPrintDialogOpen(false);
  };

  const handlePrintOptionChange = (
    option: 'includePaid' | 'includePartial' | 'includeUnpaid' | 'includeReturns'
  ) => {
    setPrintOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const handlePrint = async () => {
    if (!selectedClient) return;

    // Print from the client's FULL set, never the "Statut de paiement" view —
    // the dialog's own checkboxes are the only thing that selects content.
    //
    // One checkbox per bon state, and the states are exclusive, so a bon can
    // never be picked up twice and listed twice under the same BON N°.
    const wantExit = (exit: StockExit) => {
      const state = exitState(exit);
      return (
        (state === 'paid' && printOptions.includePaid) ||
        (state === 'partial' && printOptions.includePartial) ||
        (state === 'unpaid' && printOptions.includeUnpaid)
      );
    };

    let dataToPrint: CombinedTransaction[] = allExits
      .filter(wantExit)
      .flatMap(exit => (exit.items || []).map(item => ({
        id: exit.id,
        date: exit.date,
        bon_number: exit.bon_number,
        article_reference: item.article_reference,
        article_designation: item.article_designation,
        category: item.category,
        unit: item.unit,
        quantity: item.quantity,
        prix_sortie: Number(item.prix_sortie || 0),
        total: Number(item.total || 0),
        service_name: exit.service_name,
        is_paid: exit.is_paid,
        client_name: exit.client_name,
        type: 'exit' as const,
        // Let the PDF label each bon PAYÉ / PARTIEL / IMPAYÉ from the actual
        // amounts instead of the binary flag, so a partly-paid bon isn't
        // stamped "IMPAYÉ" on a report the user asked to be about paid bons.
        total_paid: exitPaid(exit),
        remaining: exitRemaining(exit),
      })));

    if (printOptions.includeReturns) {
      const returns = allReturns.flatMap(ret =>
        (ret.items || []).map(item => ({
          id: ret.id,
          date: ret.date,
          bon_number: ret.bon_retour_number,
          article_reference: item.article_reference,
          article_designation: item.article_designation,
          category: item.category,
          unit: item.unit,
          quantity: item.quantity,
          prix_sortie: Number(item.unit_price || 0),
          total: Number(item.total || 0),
          service_name: undefined,
          is_paid: true,
          client_name: ret.client_name,
          type: 'return' as const
        })));
      dataToPrint = [...dataToPrint, ...returns];
    }

    // Sort by date
    dataToPrint.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    // Open the PDF preview; the user prints/downloads from the viewer toolbar.
    const client = selectedClient;
    const data = dataToPrint;
    const options = printOptions;
    // The RÉSUMÉ reports on exactly the bons the report lists. Ticking "Impayé"
    // alone used to still print the client's whole impayé (12221.81), silently
    // including the outstanding half of the Partiel bons (5122.02 + 7099.79) —
    // the same figure the page shows only when the filter is on "Tous".
    //
    // The return credit is NOT scoped: it is the client's, and unticking
    // "Retours" must not quietly inflate the impayé (that was the original
    // 116912.58-vs-115425.08 bug). It is applied to the impayé leg only —
    // never to the payé leg, which on a partial selection would report a
    // refund that never happened.
    const selectedExits = allExits.filter(wantExit);
    const printPaid = selectedExits.reduce((sum, exit) => sum + exitPaid(exit), 0);
    const printUnpaid = selectedExits.reduce((sum, exit) => sum + exitRemaining(exit), 0);
    const printCredit = allReturns.reduce((sum, ret) => sum + Number(ret.grand_total || 0), 0);
    const summary = {
      totalPaid: printPaid,
      totalUnpaid: Math.max(0, printUnpaid - printCredit),
      totalReturns: printCredit,
      originalPaid: printPaid,
      originalUnpaid: printUnpaid,
    };
    openPreview({
      getBlob: async () => {
        const { generateClientSituationPDF } = await import('../../../utils/pdfGenerator');
        return generateClientSituationPDF(client, data, options, true, summary) as Blob;
      },
      fileName: `Situation_${client.code}.pdf`,
    });
    auditLogsAPI.logPdfDownload('ClientSituation', client.id, `Aperçu situation client - ${client.code}`);
    handleClosePrintDialog();
  };

  const formatNumber = (value: any, decimals: number = 2): string => {
    return Number(value || 0).toFixed(decimals);
  };

  // Combine exits and returns into a unified format
  // Flatten exits - each item becomes a separate transaction row but shares parent metadata
  const combinedTransactionsAll: CombinedTransaction[] = [
    ...stockExits.flatMap(exit =>
      (exit.items || []).map(item => ({
        id: exit.id, // Use parent ID for actions
        date: exit.date,
        bon_number: exit.bon_number,
        article_reference: item.article_reference,
        article_designation: item.article_designation,
        category: item.category,
        unit: item.unit,
        quantity: item.quantity,
        prix_sortie: Number(item.prix_sortie || 0),
        total: Number(item.total || 0),
        service_name: exit.service_name,
        is_paid: exit.is_paid,
        client_name: exit.client_name,
        type: 'exit' as const,
        total_paid: Number(exit.total_paid || 0),
        remaining: Number(exit.remaining || 0)
      }))
    ),
    ...stockReturns.flatMap(ret =>
      (ret.items || []).map(item => ({
        id: ret.id,
        date: ret.date,
        bon_number: ret.bon_retour_number,
        article_reference: item.article_reference,
        article_designation: item.article_designation,
        category: item.category,
        unit: item.unit,
        quantity: item.quantity,
        prix_sortie: Number(item.unit_price || 0),
        total: Number(item.total || 0),
        service_name: undefined,
        is_paid: true, // Returns are always considered paid (refunds)
        client_name: ret.client_name,
        type: 'return' as const
      }))
    )
  ];

  // Apply client-side column filters
  const ilike = (s: any, q: string) => (s ? String(s).toLowerCase().includes(q.toLowerCase()) : false);
  const combinedTransactions = combinedTransactionsAll.filter(t => {
    if (filters.date.from && t.date && t.date.substring(0, 10) < filters.date.from) return false;
    if (filters.date.to && t.date && t.date.substring(0, 10) > filters.date.to) return false;
    if (filters.bon_number && !ilike(t.bon_number, filters.bon_number)) return false;
    if (filters.reference && !ilike(t.article_reference, filters.reference)) return false;
    if (filters.designation && !ilike(t.article_designation, filters.designation)) return false;
    if (filters.category && !ilike(t.category, filters.category)) return false;
    if (filters.unit && !ilike(t.unit, filters.unit)) return false;
    if (filters.quantity.min !== '' && Number(t.quantity ?? 0) < Number(filters.quantity.min)) return false;
    if (filters.quantity.max !== '' && Number(t.quantity ?? 0) > Number(filters.quantity.max)) return false;
    if (filters.prix_sortie.min !== '' && Number(t.prix_sortie ?? 0) < Number(filters.prix_sortie.min)) return false;
    if (filters.prix_sortie.max !== '' && Number(t.prix_sortie ?? 0) > Number(filters.prix_sortie.max)) return false;
    if (filters.total.min !== '' && Number(t.total ?? 0) < Number(filters.total.min)) return false;
    if (filters.total.max !== '' && Number(t.total ?? 0) > Number(filters.total.max)) return false;
    if (filters.service && !ilike(t.service_name, filters.service)) return false;
    if (filters.type && t.type !== filters.type) return false;
    return true;
  });

  // Sort by date descending
  combinedTransactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Group ALL transactions by BON first (Map preserves date-sorted first-seen
  // order), THEN paginate by whole BON groups. This keeps every BON's article
  // lines together on one page so the rowSpan Date/N°Bon/Statut/Actions cells
  // are never split across a page boundary (which previously shifted columns).
  const bonGroupsMap = new Map<string, CombinedTransaction[]>();
  combinedTransactions.forEach((transaction) => {
    const bonKey = transaction.bon_number || 'NO-BON';
    if (!bonGroupsMap.has(bonKey)) {
      bonGroupsMap.set(bonKey, []);
    }
    bonGroupsMap.get(bonKey)!.push(transaction);
  });
  const bonGroups = Array.from(bonGroupsMap.values());

  // Pagination is now per-BON: "rows per page" = number of bons per page.
  const paginatedBonGroups = bonGroups.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Flatten the paginated groups with rowSpan metadata for rendering.
  const renderData: Array<{ transaction: CombinedTransaction; isFirstInBon: boolean; bonRowSpan: number }> = [];
  paginatedBonGroups.forEach((bonGroup) => {
    bonGroup.forEach((transaction, index) => {
      renderData.push({
        transaction,
        isFirstInBon: index === 0,
        bonRowSpan: bonGroup.length,
      });
    });
  });

  // Calculate totals for the client
  const computeTotals = (exits: StockExit[], returns: StockReturn[]) => {
    let totalPaid = 0;
    let totalUnpaid = 0;
    let totalReturns = 0;

    // Calculate exits totals - each stock exit has grand_total or sum of items.
    // Split by AMOUNT, not by the binary is_paid flag: a bon with an avance is
    // partly payé and partly impayé. The backend already normalises total_paid
    // (a bon manually marked is_paid=Oui is reported as fully paid), so we just
    // clamp it to the bon total and fall back to the flag on legacy payloads.
    exits.forEach(exit => {
      totalPaid += exitPaid(exit);
      totalUnpaid += exitRemaining(exit);
    });

    // Calculate returns totals - now using parent-child structure with grand_total
    returns.forEach(ret => {
      totalReturns += Number(ret.grand_total || 0);
    });

    // Apply return deduction logic:
    // 1. First deduct from Impayé (unpaid)
    // 2. Then deduct from Payé (refund)
    let remainingReturn = totalReturns;
    let adjustedUnpaid = totalUnpaid;
    let adjustedPaid = totalPaid;

    // Deduct from unpaid first
    if (remainingReturn > 0 && adjustedUnpaid > 0) {
      const deductFromUnpaid = Math.min(remainingReturn, adjustedUnpaid);
      adjustedUnpaid -= deductFromUnpaid;
      remainingReturn -= deductFromUnpaid;
    }

    // Then deduct from paid (refund)
    if (remainingReturn > 0 && adjustedPaid > 0) {
      const deductFromPaid = Math.min(remainingReturn, adjustedPaid);
      adjustedPaid -= deductFromPaid;
      remainingReturn -= deductFromPaid;
    }

    return {
      totalPaid: adjustedPaid,
      totalUnpaid: adjustedUnpaid,
      totalReturns,
      // Keep original values for description
      originalPaid: totalPaid,
      originalUnpaid: totalUnpaid
    };
  };

  // Cards describe the current "Statut de paiement" view.
  const totals = computeTotals(stockExits, stockReturns);

  return (
    <Box sx={{ px: 0 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Suivi des Clients
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Suivez les paiements et les sorties de stock par client
          </Typography>
        </Box>
        {selectedClient && (
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handleOpenPrintDialog}
            sx={{
              bgcolor: '#2196F3',
              '&:hover': {
                bgcolor: '#1976D2',
              },
            }}
          >
            Imprimer Situation
          </Button>
        )}
      </Box>

      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <Autocomplete
              options={clients}
              getOptionLabel={(option) => `${option.code} - ${option.name}`}
              value={selectedClient}
              onChange={(_, newValue) => {
                setSelectedClient(newValue);
                setStockExits([]);
                setStockReturns([]);
                setAllExits([]);
                setAllReturns([]);
                reset();
              }}
              renderInput={(params) => (
                <TextField {...params} label="Sélectionner un client" variant="filled" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="filled">
              <InputLabel>Statut de paiement</InputLabel>
              <Select
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value as 'all' | PaymentState)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="paid">Payé</MenuItem>
                <MenuItem value="partial">Partiel</MenuItem>
                <MenuItem value="unpaid">Impayé</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            {hasActive() && (
              <Button
                size="small"
                startIcon={<FilterListOffIcon />}
                onClick={reset}
                sx={{ color: 'text.secondary' }}
              >
                Effacer les filtres
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      {selectedClient && (stockExits.length > 0 || stockReturns.length > 0) && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: '#e8f5e9' }}>
              <CardContent>
                <Typography variant="h6" color="success.main">
                  Payé
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  {formatNumber(totals.totalPaid)} MAD
                </Typography>
                {totals.totalReturns > 0 && totals.originalPaid !== totals.totalPaid && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {formatNumber(totals.originalPaid)} (total payé) - {formatNumber(totals.originalPaid - totals.totalPaid)} (remboursé)
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: '#ffebee' }}>
              <CardContent>
                <Typography variant="h6" color="error.main">
                  Impayé
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  {formatNumber(totals.totalUnpaid)} MAD
                </Typography>
                {totals.totalReturns > 0 && totals.originalUnpaid !== totals.totalUnpaid && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {formatNumber(totals.originalUnpaid)} (total impayé) - {formatNumber(totals.originalUnpaid - totals.totalUnpaid)} (déduit des retours)
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: '#fff3e0' }}>
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  Retours
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  {formatNumber(totals.totalReturns)} MAD
                </Typography>
                {totals.totalReturns > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Déduit d'abord de l'impayé, puis du payé
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Table Section */}
      {selectedClient && (
        <>
          {combinedTransactions.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                {loading ? 'Chargement...' : 'Aucune transaction trouvée pour ce client'}
              </Typography>
            </Paper>
          ) : (
            <Paper>
              <TableContainer sx={getModuleTableTheme('stock')}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          Date
                          <ColumnFilter type="dateRange" value={filters.date} onChange={(v) => update('date', v)} label="date" color={STOCK_COLOR} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          BON N°
                          <ColumnFilter type="text" value={filters.bon_number} onChange={(v) => update('bon_number', v)} label="BON N°" color={STOCK_COLOR} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          Référence
                          <ColumnFilter type="text" value={filters.reference} onChange={(v) => update('reference', v)} label="référence" color={STOCK_COLOR} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          Désignation
                          <ColumnFilter type="text" value={filters.designation} onChange={(v) => update('designation', v)} label="désignation" color={STOCK_COLOR} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          Catégorie
                          <ColumnFilter type="text" value={filters.category} onChange={(v) => update('category', v)} label="catégorie" color={STOCK_COLOR} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          Unité
                          <ColumnFilter type="text" value={filters.unit} onChange={(v) => update('unit', v)} label="unité" color={STOCK_COLOR} />
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          Quantité
                          <ColumnFilter type="numberRange" value={filters.quantity} onChange={(v) => update('quantity', v)} label="quantité" color={STOCK_COLOR} />
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          PRIX S
                          <ColumnFilter type="numberRange" value={filters.prix_sortie} onChange={(v) => update('prix_sortie', v)} label="prix sortie" color={STOCK_COLOR} />
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          Total
                          <ColumnFilter type="numberRange" value={filters.total} onChange={(v) => update('total', v)} label="total" color={STOCK_COLOR} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          Service
                          <ColumnFilter type="text" value={filters.service} onChange={(v) => update('service', v)} label="service" color={STOCK_COLOR} />
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Avance / Reste</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          Statut Paiement
                          <ColumnFilter
                            type="select"
                            value={filters.type}
                            onChange={(v) => update('type', v)}
                            label="type"
                            color={STOCK_COLOR}
                            options={[
                              { value: 'exit', label: 'Sortie' },
                              { value: 'return', label: 'Retour' },
                            ]}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {renderData.map(({ transaction, isFirstInBon, bonRowSpan }, rowIndex) => {
                      const isHovered = hoveredBonNumber === transaction.bon_number;
                      const isReturn = transaction.type === 'return';
                      return (
                        <TableRow
                          // `id` is the PARENT exit/return id (shared by every article
                          // line of a Bon), so include the row index to keep keys unique —
                          // otherwise React collapses duplicate-keyed rows and the rowSpan
                          // Date/Bon cells bleed into the next group (column misalignment).
                          key={`${transaction.type}-${transaction.id}-${rowIndex}`}
                          onMouseEnter={() => setHoveredBonNumber(transaction.bon_number)}
                          onMouseLeave={() => setHoveredBonNumber(null)}
                          sx={{
                            backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                            transition: 'background-color 0.15s ease',
                            '& td': {
                              backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                            },
                          }}
                        >
                          {/* Date column - only render for first article in BON */}
                          {isFirstInBon && (
                            <TableCell
                              rowSpan={bonRowSpan}
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                                transition: 'background-color 0.15s ease',
                              }}
                            >
                              {transaction.date ? new Date(transaction.date).toLocaleDateString('fr-FR') : '-'}
                            </TableCell>
                          )}

                          {/* BON N° column - only render for first article in BON */}
                          {isFirstInBon && (
                            <TableCell
                              rowSpan={bonRowSpan}
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                                transition: 'background-color 0.15s ease',
                                fontWeight: 'bold',
                              }}
                            >
                              {transaction.bon_number}
                            </TableCell>
                          )}

                          {/* Article columns - render for each article */}
                          <TableCell sx={{ backgroundColor: 'inherit' }}>
                            {transaction.article_reference}
                          </TableCell>
                          <TableCell sx={{ backgroundColor: 'inherit' }}>
                            {transaction.article_designation}
                          </TableCell>
                          <TableCell sx={{ backgroundColor: 'inherit' }}>
                            <Chip label={transaction.category} size="small" variant="filled" />
                          </TableCell>
                          <TableCell sx={{ backgroundColor: 'inherit' }}>{transaction.unit}</TableCell>
                          <TableCell align="right" sx={{ backgroundColor: 'inherit' }}>
                            {transaction.quantity || 0}
                          </TableCell>
                          <TableCell align="right" sx={{ backgroundColor: 'inherit' }}>
                            {formatNumber(transaction.prix_sortie)}
                          </TableCell>
                          <TableCell align="right" sx={{ backgroundColor: 'inherit' }}>
                            <strong>{formatNumber(transaction.total)}</strong>
                          </TableCell>
                          <TableCell sx={{ backgroundColor: 'inherit' }}>
                            {transaction.service_name && (
                              <Chip label={transaction.service_name} size="small" color="primary" variant="filled" />
                            )}
                          </TableCell>

                          {/* Avance / Reste - Bon-level figure, only for first article in BON */}
                          {isFirstInBon && (
                            <TableCell
                              rowSpan={bonRowSpan}
                              align="right"
                              sx={{
                                verticalAlign: 'middle',
                                whiteSpace: 'nowrap',
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                                transition: 'background-color 0.15s ease',
                              }}
                            >
                              {isReturn ? (
                                '-'
                              ) : (
                                <>
                                  <div>{formatNumber(transaction.total_paid)}</div>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 600,
                                      color: Number(transaction.remaining || 0) > 0 ? '#f44336' : '#4caf50',
                                    }}
                                  >
                                    Reste {formatNumber(transaction.remaining)}
                                  </Typography>
                                </>
                              )}
                            </TableCell>
                          )}

                          {/* Payment Status - only render for first article in BON */}
                          {isFirstInBon && (
                            <TableCell
                              rowSpan={bonRowSpan}
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                                transition: 'background-color 0.15s ease',
                              }}
                            >
                              {isReturn ? (
                                <Chip
                                  label="Retour"
                                  sx={{
                                    bgcolor: '#ff9800',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                  }}
                                  size="small"
                                />
                              ) : (
                                (() => {
                                  const state = paymentState(transaction);
                                  const { label, color } = PAYMENT_CHIP[state];
                                  return (
                                    <Chip
                                      label={label}
                                      sx={{
                                        bgcolor: color,
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        '& .MuiChip-icon': {
                                          color: 'white',
                                        },
                                      }}
                                      size="small"
                                      icon={
                                        state === 'paid' ? <PaidIcon />
                                          : state === 'partial' ? <PartialIcon />
                                            : <UnpaidIcon />
                                      }
                                    />
                                  );
                                })()
                              )}
                            </TableCell>
                          )}

                          {/* Actions column - only render for first article in BON and only for exits */}
                          {isFirstInBon && (
                            <TableCell
                              rowSpan={bonRowSpan}
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                                transition: 'background-color 0.15s ease',
                              }}
                            >
                              {!isReturn && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => {
                                    // Find the original exit to pass to the dialog
                                    const originalExit = stockExits.find(e => e.bon_number === transaction.bon_number);
                                    if (originalExit) handleOpenConfirmDialog(originalExit);
                                  }}
                                  sx={{
                                    bgcolor: '#2196F3',
                                    color: 'white',
                                    minWidth: 160,
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    textTransform: 'none',
                                    '&:hover': {
                                      bgcolor: '#1976D2',
                                    },
                                  }}
                                >
                                  {transaction.is_paid ? 'Marquer comme impayé' : 'Marquer comme payé'}
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <CustomTablePagination
                count={bonGroups.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          )}
        </>
      )}

      {!selectedClient && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Sélectionnez un client pour voir ses sorties de stock
          </Typography>
        </Paper>
      )}

      {/* Confirmation Dialog */}
      {exitToToggle && (
        <AlertDialog
          open={confirmDialogOpen}
          type="warning"
          title={exitToToggle.is_paid ? 'Annuler le paiement' : 'Confirmer le paiement'}
          message={
            exitToToggle.is_paid
              ? `Êtes-vous sûr de vouloir annuler le paiement du BON ${exitToToggle.bon_number} ?\n\nClient: ${exitToToggle.client_name}\nTotal: ${formatNumber(
                  exitToToggle.grand_total || (exitToToggle.items || []).reduce((sum, item) => sum + Number(item.total || 0), 0)
                )} MAD`
              : `Êtes-vous sûr de vouloir confirmer le paiement du BON ${exitToToggle.bon_number} ?\n\nClient: ${exitToToggle.client_name}\nTotal: ${formatNumber(
                  exitToToggle.grand_total || (exitToToggle.items || []).reduce((sum, item) => sum + Number(item.total || 0), 0)
                )} MAD`
          }
          onClose={handleCloseConfirmDialog}
          onConfirm={handleConfirmTogglePaymentStatus}
          confirmText={exitToToggle.is_paid ? 'Annuler' : 'Confirmer'}
          cancelText="Retour"
          showCancel={true}
        />
      )}

      {/* Alert Dialog */}
      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      {/* Print Options Dialog */}
      <Dialog
        open={printDialogOpen}
        onClose={handleClosePrintDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Options d'impression - Situation du Client
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sélectionnez les informations à inclure dans le rapport:
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={printOptions.includePaid}
                    onChange={() => handlePrintOptionChange('includePaid')}
                    sx={{
                      color: '#4caf50',
                      '&.Mui-checked': {
                        color: '#4caf50',
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label="Payé"
                      size="small"
                      sx={{ bgcolor: '#4caf50', color: 'white' }}
                    />
                    <Typography variant="body2">Sorties payées</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={printOptions.includePartial}
                    onChange={() => handlePrintOptionChange('includePartial')}
                    sx={{
                      color: PAYMENT_CHIP.partial.color,
                      '&.Mui-checked': {
                        color: PAYMENT_CHIP.partial.color,
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={PAYMENT_CHIP.partial.label}
                      size="small"
                      sx={{ bgcolor: PAYMENT_CHIP.partial.color, color: 'white' }}
                    />
                    <Typography variant="body2">Sorties partiellement payées</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={printOptions.includeUnpaid}
                    onChange={() => handlePrintOptionChange('includeUnpaid')}
                    sx={{
                      color: '#f44336',
                      '&.Mui-checked': {
                        color: '#f44336',
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label="Impayé"
                      size="small"
                      sx={{ bgcolor: '#f44336', color: 'white' }}
                    />
                    <Typography variant="body2">Sorties impayées</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={printOptions.includeReturns}
                    onChange={() => handlePrintOptionChange('includeReturns')}
                    sx={{
                      color: '#ff9800',
                      '&.Mui-checked': {
                        color: '#ff9800',
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label="Retour"
                      size="small"
                      sx={{ bgcolor: '#ff9800', color: 'white' }}
                    />
                    <Typography variant="body2">Retours de stock</Typography>
                  </Box>
                }
              />
            </FormGroup>
            {!printOptions.includePaid && !printOptions.includePartial && !printOptions.includeUnpaid && !printOptions.includeReturns && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                Veuillez sélectionner au moins une option
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClosePrintDialog} color="inherit">
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handlePrint}
            disabled={!printOptions.includePaid && !printOptions.includePartial && !printOptions.includeUnpaid && !printOptions.includeReturns}
            startIcon={<PrintIcon />}
            sx={{
              bgcolor: '#2196F3',
              '&:hover': {
                bgcolor: '#1976D2',
              },
            }}
          >
            Imprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default ClientTracking;
